import { useState } from 'react'
import { Dialog } from 'radix-ui'
import { FileText, Image as ImageIcon, Sheet, Trash2 } from 'lucide-react'
import { useCreateDocumentType, useUpdateDocumentType } from '../hooks'
import type { DocumentType } from '../types'

const CODE_REGEX = /^[a-z][a-z0-9_]{0,63}$/
const APPEND_SORT_ORDER = 1_000_000

/** Presety dla typowych grup rozszerzeń. Klik łączy z aktualną listą (bez duplikatów). */
const EXTENSION_PRESETS = [
  {
    key: 'images',
    label: 'Obrazki',
    icon: ImageIcon,
    extensions: ['.jpg', '.jpeg', '.png', '.webp', '.gif', '.heic'],
  },
  {
    key: 'documents',
    label: 'Dokumenty',
    icon: FileText,
    extensions: ['.pdf', '.doc', '.docx', '.odt', '.txt', '.rtf'],
  },
  {
    key: 'spreadsheets',
    label: 'Arkusze',
    icon: Sheet,
    extensions: ['.xls', '.xlsx', '.csv', '.ods'],
  },
] as const

/** Łączy CSV listy rozszerzeń, normalizuje (lowercase, prefix kropką) i usuwa duplikaty. */
function mergeExtensions(current: string, addition: readonly string[]): string {
  const norm = (raw: string) => {
    const t = raw.trim().toLowerCase()
    if (!t) return ''
    return t.startsWith('.') ? t : '.' + t
  }
  const set = new Set<string>()
  current.split(',').map(norm).filter(Boolean).forEach(e => set.add(e))
  addition.map(norm).filter(Boolean).forEach(e => set.add(e))
  return Array.from(set).join(',')
}

export function DocumentTypeFormDialog({ configurationCode, editing, onClose }: {
  configurationCode: string
  editing: DocumentType | null
  onClose: () => void
}) {
  const create = useCreateDocumentType(configurationCode)
  const update = useUpdateDocumentType(configurationCode)

  const [code, setCode] = useState(editing?.code ?? '')
  const [labelPl, setLabelPl] = useState(editing?.labelPl ?? '')
  const [description, setDescription] = useState(editing?.description ?? '')
  const [isActive, setIsActive] = useState(editing?.isActive ?? true)
  const [acceptedExtensions, setAcceptedExtensions] = useState<string>(editing?.acceptedExtensions ?? '')
  const [error, setError] = useState<string | null>(null)

  const submit = () => {
    setError(null)
    if (!editing && !CODE_REGEX.test(code)) {
      setError('Code: małe litery, cyfry, podkreślenia. Musi zaczynać się od litery.')
      return
    }
    if (!labelPl.trim()) { setError('Etykieta nie może być pusta.'); return }

    const ext = acceptedExtensions.trim() === '' ? null : acceptedExtensions.trim()

    if (editing) {
      update.mutate(
        { id: editing.id, payload: { labelPl, description: description || null, sortOrder: editing.sortOrder, isActive, acceptedExtensions: ext } },
        { onSuccess: onClose, onError: (e) => setError(formatError(e)) },
      )
    } else {
      create.mutate(
        { code, labelPl, description: description || null, sortOrder: APPEND_SORT_ORDER, isActive, acceptedExtensions: ext },
        { onSuccess: onClose, onError: (e) => setError(formatError(e)) },
      )
    }
  }

  const isSubmitting = create.isPending || update.isPending

  return (
    <Dialog.Root open onOpenChange={o => { if (!o) onClose() }}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/40" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 -translate-x-1/2 -translate-y-1/2 bg-white rounded-lg p-5 w-[440px] shadow-xl">
          <Dialog.Title className="text-base font-semibold">
            {editing ? `Edycja typu ${editing.code}` : 'Nowy typ dokumentu'}
          </Dialog.Title>

          <div className="mt-4 flex flex-col gap-3 text-sm">
            <label className="grid gap-1">
              <span className="text-xs uppercase text-slate-500">Kod</span>
              <input
                value={code}
                onChange={e => setCode(e.target.value)}
                disabled={!!editing || isSubmitting}
                className="border rounded px-2 py-1 font-mono"
                placeholder="skan_umowy, dowod_osobisty, ..."
              />
            </label>
            <label className="grid gap-1">
              <span className="text-xs uppercase text-slate-500">Etykieta</span>
              <input value={labelPl} onChange={e => setLabelPl(e.target.value)} disabled={isSubmitting} className="border rounded px-2 py-1" />
            </label>
            <label className="grid gap-1">
              <span className="text-xs uppercase text-slate-500">Opis (opcjonalny)</span>
              <textarea value={description} onChange={e => setDescription(e.target.value)} disabled={isSubmitting} rows={2} className="border rounded px-2 py-1" />
            </label>
            <label className="grid gap-1">
              <span className="text-xs uppercase text-slate-500">Akceptowane rozszerzenia</span>
              <input
                value={acceptedExtensions}
                onChange={e => setAcceptedExtensions(e.target.value)}
                disabled={isSubmitting}
                className="border rounded px-2 py-1 font-mono text-xs"
                placeholder=".pdf, .jpg, .png"
              />
              <div className="flex flex-wrap items-center gap-1.5 mt-1">
                <span className="text-[10px] text-slate-500">Szybko:</span>
                {EXTENSION_PRESETS.map(preset => {
                  const Icon = preset.icon
                  return (
                    <button
                      key={preset.key}
                      type="button"
                      onClick={() => setAcceptedExtensions(prev => mergeExtensions(prev, preset.extensions))}
                      disabled={isSubmitting}
                      className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white hover:bg-sky-50 hover:border-sky-300 hover:text-sky-700 text-slate-600 px-2 py-0.5 text-[11px] font-medium transition-colors disabled:opacity-50"
                      title={preset.extensions.join(', ')}
                    >
                      <Icon size={11} />
                      {preset.label}
                    </button>
                  )
                })}
                {acceptedExtensions && (
                  <button
                    type="button"
                    onClick={() => setAcceptedExtensions('')}
                    disabled={isSubmitting}
                    className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white hover:bg-rose-50 hover:border-rose-300 hover:text-rose-700 text-slate-500 px-2 py-0.5 text-[11px] font-medium transition-colors ml-auto"
                  >
                    <Trash2 size={11} />
                    Wyczyść
                  </button>
                )}
              </div>
              <span className="text-[10px] text-slate-400">
                CSV listy rozszerzeń. Puste = wszystkie dozwolone. Backend egzekwuje tę listę przy każdym uploadzie.
              </span>
            </label>
            <label className="flex items-center gap-2">
              <input type="checkbox" checked={isActive} onChange={e => setIsActive(e.target.checked)} disabled={isSubmitting} />
              <span>Aktywny</span>
            </label>
            <p className="text-[10px] text-slate-400">Kolejność: przeciągnij na liście.</p>

            {error && <p className="text-red-600 text-xs">{error}</p>}

            <div className="flex justify-end gap-2 mt-2">
              <button type="button" onClick={onClose} className="px-3 py-1.5 border rounded" disabled={isSubmitting}>Anuluj</button>
              <button type="button" onClick={submit} className="px-3 py-1.5 bg-sky-600 text-white rounded disabled:opacity-50" disabled={isSubmitting}>
                {isSubmitting ? 'Zapisywanie...' : 'Zapisz'}
              </button>
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}

function formatError(err: unknown): string {
  const ax = err as { response?: { data?: { detail?: string; title?: string } } }
  return ax.response?.data?.detail ?? ax.response?.data?.title ?? 'Błąd zapisu'
}
